import React from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { FormMenu } from "../../../../components/Buttons";
import ItemListManager from "../../../../components/ItemListManager";
import RegionForm from "./RegionForm";
import TablePageReport from "../../../../components/TablePageReport";
import { isMasterOrDL, TableEmptyMessage } from "../../../../components/Utils";
import { PermissionListFetcher, Resources } from "../../../Permissions";
import { isResourceReadable } from "../../../Permissions/actions";

const RegionListForm = React.forwardRef(function RegionListForm(
  { className = "", ...restProps },
  ref,
) {
  const regionsRef = React.useRef(null);

  React.useImperativeHandle(ref, () => ({
    getModel: () => {
      const regions = regionsRef.current.getModel();
      const model = [...regions];
      if (model.length === 0) {
        regionsRef.current.toggleError(true);
        return null;
      } else {
        regionsRef.current.toggleError(false);
      }
      return model;
    },
    toggleAddition: (disabled) => regionsRef.current.toggleAddition(disabled),
    setDebitors: (debitors) => regionsRef.current.setDebitors(debitors),
  }));

  return (
    <div className={className}>
      <PermissionListFetcher
        resources={[Resources.Organisation.CompanyWizard.RegionStorageLocation]}
      >
        <Region ref={regionsRef} {...restProps} />
      </PermissionListFetcher>
    </div>
  );
});

export default RegionListForm;

const Region = React.forwardRef(function Region(
  {
    model = [],
    regions: regionList,
    debitors,
    getBaseData,
    onChange,
    disabled: _disabled,
    readOnly,
    rows,
    className,
    permissions,
  },
  ref,
) {
  const [state, setState] = React.useState(() => {
    const items = model || [];
    const regions = (regionList || []).filter((region) => {
      let notFound = true;
      for (const item of items) {
        if (item.name === region) {
          notFound = false;
          break;
        }
      }
      return notFound;
    });
    return {
      items: items.map((item, index) => ({ ...item, index })),
      regions,
      addDisabled: false,
      error: null,
      debitors: debitors || [],
      first: 0,
    };
  });

  const formRef = React.useRef(null);

  React.useImperativeHandle(ref, () => ({
    getModel: () => {
      const { items } = state;
      const model = items.map((item) => {
        const {
          id,
          name,
          debitorKey,
          debitorMax,
          companyName,
          address,
          storageLocation,
        } = item;
        const region = {
          id,
          name,
          debitorKey,
          debitorMax,
          companyName,
          address,
          storageLocation,
        };
        return region;
      });
      return model;
    },
    toggleAddition: (disabled) =>
      setState((prev) => ({ ...prev, addDisabled: disabled })),
    setDebitors: (debitors) => setState((prev) => ({ ...prev, debitors })),
    toggleError: (error) => setState((prev) => ({ ...prev, error })),
  }));

  const onSave = (onHide) => {
    const model = formRef.current.getModel();
    if (!model) return;
    const items = state.items;
    if (model.index !== undefined && items[model.index] !== undefined) {
      items[model.index] = model;
    } else {
      model.index = items.length;
      items.push(model);
    }
    const regions = state.regions.filter((item) => item !== model.name);
    const addDisabled = regions.length === 0;
    setState((prev) => ({ ...prev, items, regions, addDisabled }));
    onChange && onChange(items);
    onHide && onHide();
  };
  const onDelete = (selected) => {
    const items = [];
    state.items.forEach((item) => {
      if (item.name !== selected.name) {
        items.push(item);
      }
    });
    setState((prev) => ({ ...prev, items }));
    onChange && onChange(items);
  };

  const disabled = readOnly || _disabled;
  const showStorageLocation = isResourceReadable(
    Resources.Organisation.CompanyWizard.RegionStorageLocation,
    permissions,
  );
  const { regions, debitors: stateDebitors, first } = state;
  const onPage = (event) => {
    const firstPage = event.first;
    setState((prev) => ({ ...prev, first: firstPage }));
  };
  const getMinDebitorsProRegion = (region) => {
    const regionDebitors = stateDebitors.filter(
      (debitor) => debitor.region === region.name,
    );
    return regionDebitors.length;
  };
  const isDeleteDisabled = (region) => {
    const regionDebitors = stateDebitors.filter(
      (debitor) => debitor.region === region.name,
    );
    return regionDebitors.length > 0;
  };
  const renderItemTable = (items, actionTemplate) => {
    const data = items
      .map((item, index) => ({ ...item, index }))
      .sort((a, b) => {
        if (a.name < b.name) return -1;
        else if (a.name > b.name) return 1;
        return 0;
      });
    let columns = [
      <Column
        key="col0"
        field="name"
        header="Region"
        sortable={data.length > 1}
        filter={data.length > rows}
        filterMatchMode="contains"
        style={{ textAlign: "center" }}
      />,
      <Column
        key="col2"
        field="debitorKey"
        header="Kennung"
        sortable={data.length > 1}
        filter={data.length > rows}
        filterMatchMode="contains"
        style={{ textAlign: "center" }}
      />,
      <Column
        key="col3"
        field="debitorMax"
        header="Maximale Debitorenanzahl"
        sortable={data.length > 1}
        filter={data.length > rows}
        filterMatchMode="contains"
        style={{ textAlign: "center" }}
      />,
      <Column
        key="col4"
        body={actionTemplate}
        style={{ textAlign: "center", width: "8em" }}
      />,
    ];
    if (showStorageLocation) {
      columns = [
        ...columns.slice(0, 1),
        <Column
          key="col1"
          field="storageLocation"
          header="Storage Location"
          sortable={data.length > 1}
          filter={data.length > rows}
          filterMatchMode="contains"
          style={{
            textAlign: "center",
            width: showStorageLocation ? "0px" : "auto",
          }}
        />,
        ...columns.slice(1),
      ];
    }
    return (
      <DataTable
        value={data}
        rows={rows}
        paginator={data.length > rows}
        first={first}
        onPage={onPage}
        paginatorLeft={
          <TablePageReport
            className="pad-lft"
            totalRecords={data.length}
            filteredRecords={data.length}
            first={first}
            rows={rows}
            emptyText="Keine Regionen"
            itemName="Region"
            itemNamePlural="Regionen"
          />
        }
        emptyMessage={<TableEmptyMessage itemNamePlural="Regionen" min />}
      >
        {columns}
      </DataTable>
    );
  };
  const renderItemForm = (item, onHide) => {
    const region = item || {
      name: "",
      debitorKey: "",
      debitorMax: 10,
    };
    const baseData = getBaseData ? getBaseData() : {};
    return (
      <div>
        <RegionForm
          ref={formRef}
          key={Math.random()}
          model={region}
          regions={regions}
          isCompanySpecialKDL={isMasterOrDL(baseData)}
          disabled={disabled}
          {...baseData}
          minNrDebitors={getMinDebitorsProRegion(region)}
        />
        <FormMenu
          className="margin-big-top w3-border-top pad-big w3-light-grey"
          style={{
            marginLeft: "-1em",
            marginRight: "-1em",
            marginBottom: "-1em",
          }}
          onSave={() => onSave(onHide)}
          onCancel={onHide}
          saveHidden={disabled}
          saveBtnName="saveRegionBtn"
          cancelBtnName="cancelRegionBtn"
          cancelBtnLabel={disabled ? "Schließen" : undefined}
        />
      </div>
    );
  };
  const getItemFormTitle = (region) => {
    let title = "hinzufügen";
    if (region) {
      title = disabled ? "anzeigen" : "bearbeiten";
    }
    return <h3 className="no-margin">{`Region ${title}`}</h3>;
  };
  const titleClassName = `no-margin-top`;
  return (
    <ItemListManager
      key={Math.random()}
      className={className}
      title={<h3 className={titleClassName}>Regionen</h3>}
      addBtnLabel="Hinzufügen"
      addBtnName="addCompanyRegionBtn"
      addBtnDisabled={state.addDisabled}
      addBtnHidden={disabled}
      items={state.items}
      getItemFormTitle={getItemFormTitle}
      renderItemTable={renderItemTable}
      renderItemForm={renderItemForm}
      namePrefix="region"
      itemVisibleOnly={disabled}
      isDeleteDisabled={isDeleteDisabled}
      hideDisabledDelete
      itemDeleteable={!disabled}
      deleteTitle="Region löschen"
      getDeleteText={(item) =>
        `Wollen Sie die Region ${item.name} unwiderruflich löschen?`
      }
      onDelete={onDelete}
    />
  );
});
